import * as st from './st';
import { MAGING_BONUS_CAPS, magingBonusExcess, magingDelta } from './itemMaging';

const TOTAL_SLOTS = 16; // server expects items[0..15]
const RING_SLOTS = [3, 4];
const TROPHUS_SLOTS = [10, 11, 12, 13, 14, 15];

// Trophy set-bonus condition, Dofus 3.7 (`pk<2`) — mirrors
// RESTRICTED_TROPHY_MAX_SETS / SET_EQUIPPED_MIN_PIECES in core/enums/criteria.py
// and solver-core/src/valid.rs. A trophy carrying the condition may be worn with
// strictly fewer equipped sets than the first; a set counts as equipped at the
// second. Pre-3.7 counted bonus tiers instead, so a 4-piece panoply was three
// bonuses; it is now one set.
const RESTRICTED_TROPHY_MAX_SETS = 2;
const SET_EQUIPPED_MIN_PIECES = 2;

// ── Invalid-build wording (game vocabulary: see docs/i18n-glossary.md) ──
// `{item}` resolves to the offending item's name, or TXT_SOME_ITEM when it is
// unknown. The generic fallback is lowercase, so formatReason() capitalizes the
// final sentence — templates are free to open with `{item}`.
const TXT_TITLE_BLOCKING = { fr: 'Génération bloquée', en: 'Generation blocked', de: 'Generierung blockiert', es: 'Generación bloqueada', pt: 'Geração bloqueada' };
const TXT_TITLE_WARNING = { fr: 'Stuff invalide', en: 'Invalid build', de: 'Ungültiger Build', es: 'Build inválida', pt: 'Build inválida' };
const TXT_HINT_BLOCKING = { fr: 'À corriger pour générer.', en: 'Fix this to generate.', de: 'Zum Generieren beheben.', es: 'Corrígelo para generar.', pt: 'Corrija para gerar.' };
const TXT_SOME_ITEM = { fr: 'cet équipement', en: 'this item', de: 'dieser Ausrüstung', es: 'este equipo', pt: 'este equipamento' };

const TXT_REASON = {
    OVER_LEVEL_ITEM: {
        fr: 'Niveau de {item} ({itemLevel}) supérieur au stuff ({reqLvl}).',
        en: 'Level of {item} ({itemLevel}) is above the build ({reqLvl}).',
        de: 'Stufe von {item} ({itemLevel}) über der Build-Stufe ({reqLvl}).',
        es: 'Nivel de {item} ({itemLevel}) superior al de la build ({reqLvl}).',
        pt: 'Nível de {item} ({itemLevel}) superior ao da build ({reqLvl}).',
    },
    DUPLICATE_SET_RING: {
        fr: 'Les deux anneaux sont {item} : ils doivent être différents.',
        en: 'Both rings are {item}: they must be different.',
        de: 'Beide Ringe sind {item}: Sie müssen unterschiedlich sein.',
        es: 'Ambos anillos son {item}: deben ser diferentes.',
        pt: 'Os dois anéis são {item}: devem ser diferentes.',
    },
    DUPLICATE_TROPHUS_DOFUS: {
        fr: 'Doublon de {item} : chaque trophée ou dofus doit être unique.',
        en: 'Duplicate {item}: each trophy or dofus must be unique.',
        de: 'Doppeltes {item}: Trophäen und Dofus müssen einzigartig sein.',
        es: 'Duplicado de {item}: cada trofeo o dofus debe ser único.',
        pt: 'Duplicado de {item}: cada troféu ou dofus deve ser único.',
    },
    RESTRICTED_TROPHY_SET_BONUS: {
        fr: 'Trophée restreint : une seule panoplie équipée autorisée (actuellement {equippedSetCount}).',
        en: 'Restricted trophy: only one equipped set allowed (currently {equippedSetCount}).',
        de: 'Eingeschränkte Trophäe: nur ein ausgerüstetes Set erlaubt (aktuell {equippedSetCount}).',
        es: 'Trofeo restringido: solo se permite un set equipado (actualmente {equippedSetCount}).',
        pt: 'Troféu restrito: apenas um conjunto equipado é permitido (atualmente {equippedSetCount}).',
    },
    AP_MP_RESTRICTED: {
        fr: '{item} incompatible avec {ap} PA et {mp} PM.',
        en: '{item} cannot be equipped with {ap} AP and {mp} MP.',
        de: '{item} nicht mit {ap} AP und {mp} BP kombinierbar.',
        es: '{item} incompatible con {ap} PA y {mp} PM.',
        pt: '{item} incompatível com {ap} PA e {mp} PM.',
    },
    MAGING_BONUS_OVER: {
        fr: 'La forgemagie globale dépasse la limite de 1PA, 1PM, 1PO.',
        en: 'Total smithmagic exceeds the limit of 1AP, 1MP, 1 Range.',
        de: 'Die gesamte Schmiedemagie überschreitet das Limit von 1AP, 1BP, 1RW.',
        es: 'La forjamagia global supera el límite de 1PA, 1PM, 1AL.',
        pt: 'A forjamagia global ultrapassa o limite de 1PA, 1PM, 1AL.',
    },
    ITEM_CRITERIA_UNMET: {
        fr: 'Conditions d’équipement de {item} non remplies.',
        en: 'Equip requirements for {item} not met.',
        de: 'Ausrüstungsbedingungen für {item} nicht erfüllt.',
        es: 'Requisitos de equipamiento de {item} no cumplidos.',
        pt: 'Requisitos de equipamento de {item} não cumpridos.',
    },
};

const TXT_REASON_FALLBACK = {
    fr: 'Stuff inutilisable tel quel par le générateur.',
    en: 'Build unusable as-is by the generator.',
    de: 'Build so nicht vom Generator verwendbar.',
    es: 'Build inutilizable tal cual por el generador.',
    pt: 'Build inutilizável como está pelo gerador.',
};

const pick = (txt, lang) => txt[lang] || txt.en;

// Reasons whose sentence says it all: the build is still usable, so the
// "Invalid build" title would be wrong.
const UNTITLED_REASONS = new Set(['MAGING_BONUS_OVER']);

const toFiniteNum = (v) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
};

function getItemLevel(item) {
    if (!item) return 0;
    const lvl = item.level ?? item.lvl ?? item.lvl_min;
    const n = toFiniteNum(lvl);
    return n == null ? 0 : n;
}

function getItemId(item) {
    if (!item || item.id == null) return null;
    const n = toFiniteNum(item.id);
    return n == null ? String(item.id) : String(n);
}

function getItemSetId(item) {
    if (!item) return 0;
    const n = toFiniteNum(item.item_set_id ?? 0);
    return n == null ? 0 : n;
}

function extractCriteriaConditions(item) {
    const raw = item?.criteria;
    if (!Array.isArray(raw) || raw.length === 0) return [];

    const out = [];
    for (const group of raw) {
        const conds = group?.conditions;
        if (!Array.isArray(conds)) continue;
        for (const c of conds) {
            const characteristic = toFiniteNum(c?.characteristic);
            const value = toFiniteNum(c?.value);
            if (characteristic == null || value == null) continue;

            out.push({
                characteristic,
                operator: c?.operator != null ? String(c.operator) : null,
                value,
            });
        }
    }
    return out;
}

function isLessOp(op) {
    if (!op) return false;
    const s = String(op).trim();
    return s === '<' || s === '<=';
}

function evaluateCriterionCondition(condition, stats, lvl) {
    const characteristic = toFiniteNum(condition?.characteristic);
    const targetValue = toFiniteNum(condition?.value);
    const operator = condition?.operator != null ? String(condition.operator).trim() : '';
    if (characteristic == null || targetValue == null) return true;
    let currentValue = toFiniteNum(stats?.[characteristic]?.current);
    if (currentValue == null) return true;

    // `Vitalité > 3949` (Gargandyas gear) is a threshold on the Vitality characteristic,
    // not on HP: in game you clear it with 3950 Vitality, which a level 200 character
    // wears as 5000 HP. Our vitality row is the HP total, so the base PV the level hands
    // out for free comes off before comparing — same as the backend and the Rust kernel.
    if (characteristic === st.VITALITY_ID) {
        currentValue -= st.getBaseVitality(lvl);
    }

    if (operator === '>') return currentValue > targetValue;
    if (operator === '>=') return currentValue >= targetValue;
    if (operator === '<') return currentValue < targetValue;
    if (operator === '<=') return currentValue <= targetValue;
    if (operator === '=') return currentValue === targetValue;
    if (operator === '!=') return currentValue !== targetValue;
    return true;
}

function evaluateCriteriaGroup(group, stats, lvl) {
    const conditions = Array.isArray(group?.conditions) ? group.conditions : [];
    const operators = Array.isArray(group?.operators) ? group.operators : [];
    if (!conditions.length) return true;

    let result = evaluateCriterionCondition(conditions[0], stats, lvl);
    for (let i = 1; i < conditions.length; i++) {
        const op = operators[i - 1];
        const condOk = evaluateCriterionCondition(conditions[i], stats, lvl);
        if (op === '|') result = result || condOk;
        else result = result && condOk;
    }
    return result;
}

/**
 * Backend nuance: some items are flagged "AP/MP restricted" based on criteria like:
 *   AP < 12 OR MP < 6
 *
 * We mirror detection by inspecting the item.criteria conditions.
 */
function isApMpRestrictedItem(item) {
    if (!item || typeof item !== 'object') return false;
    // Some item payloads may already provide this boolean flag.
    if (item.ap_mp_restricted === true) return true;
    if (item.apMpRestricted === true) return true;
    if (item.restricted_ap_mp === true) return true;

    const conds = extractCriteriaConditions(item);
    if (!conds.length) return false;

    const hasAp = conds.some(
        (c) => c.characteristic === st.AP_ID && isLessOp(c.operator) && c.value === 12
    );
    const hasMp = conds.some(
        (c) => c.characteristic === st.MP_ID && isLessOp(c.operator) && c.value === 6
    );
    return hasAp || hasMp;
}

function findFirstUnmetItemCriteria(items, stats, lvl) {
    for (let slot = 0; slot < items.length; slot++) {
        const item = items[slot];
        const groups = Array.isArray(item?.criteria) ? item.criteria : [];
        if (!groups.length) continue;
        const allGroupsSatisfied = groups.every((group) => evaluateCriteriaGroup(group, stats, lvl));
        if (!allGroupsSatisfied) {
            return { slot, item };
        }
    }
    return null;
}

function countEquippedSets(build) {
    const items = Array.isArray(build?.items) ? build.items : [];
    const counts = {};

    for (const item of items) {
        if (!item) continue;
        const setId = getItemSetId(item);
        if (setId <= 0) continue;
        counts[setId] = (counts[setId] || 0) + 1;
    }

    return Object.values(counts).filter((pieces) => pieces >= SET_EQUIPPED_MIN_PIECES).length;
}

function isSlotLocked(lockedSlots, slot) {
    if (slot == null) return false;
    if (!lockedSlots || typeof lockedSlots !== 'object') return false;
    return Boolean(lockedSlots[slot] || lockedSlots[String(slot)]);
}

/**
 * Return true only when the current invalidity is not safely recoverable by
 * filling/replacing unlocked slots. Complex stat-threshold conflicts are left
 * to the solver, which validates final candidate builds.
 *
 * @param {{ code?: string, slot?: number, slots?: number[] } | null | undefined} reason
 * @param {Record<string, any>} lockedSlots
 */
function isBlockingInitialBuildReason(reason, lockedSlots = {}) {
    if (!reason || typeof reason !== 'object') return false;

    switch (reason.code) {
        case 'OVER_LEVEL_ITEM':
            return true;

        case 'DUPLICATE_SET_RING':
        case 'DUPLICATE_TROPHUS_DOFUS': {
            const slots = Array.isArray(reason.slots)
                ? reason.slots
                : (reason.slot != null ? [reason.slot] : []);
            if (!slots.length) return true;
            return slots.every((slot) => isSlotLocked(lockedSlots, slot));
        }

        // MAGING_BONUS_OVER is not the solver's to fix and nothing to stop for:
        // the sheet already ignores the extra point. A warning, so the user
        // knows it is wasted.
        case 'RESTRICTED_TROPHY_SET_BONUS':
        case 'AP_MP_RESTRICTED':
        case 'MAGING_BONUS_OVER':
        case 'ITEM_CRITERIA_UNMET':
            return false;

        default:
            return true;
    }
}

/**
 * Client-side validity for `initial_build` (level, duplicates, restricted rules).
 * Duplicate rings/trophies are invalid even when those slots are locked, so the UI always warns.
 *
 * @param {import('./Build').default} build
 * @param {{ reqLvl: number }} params
 * @returns {{ valid: true } | { valid: false, reason: { code: string, [k: string]: any } }}
 */
function validateBackendInitialBuild(build, { reqLvl } = {}) {
    const items = Array.isArray(build?.items) ? build.items.slice(0, TOTAL_SLOTS) : [];
    const safeReqLvl = typeof reqLvl === 'number' && Number.isFinite(reqLvl) ? reqLvl : 0;
    const stats = typeof build?.getStats === 'function' ? build.getStats() : null;

    // 1) Over-level item
    for (let slot = 0; slot < TOTAL_SLOTS; slot++) {
        const item = items[slot];
        if (!item || typeof item !== 'object') continue;
        const itemLvl = getItemLevel(item);
        if (itemLvl > safeReqLvl) {
            return {
                valid: false,
                reason: {
                    code: 'OVER_LEVEL_ITEM',
                    slot,
                    itemId: getItemId(item),
                    itemLevel: itemLvl,
                    reqLvl: safeReqLvl,
                },
            };
        }
    }

    // 2) Duplicate set ring (slots 3 and 4)
    const ringLeft = items[RING_SLOTS[0]];
    const ringRight = items[RING_SLOTS[1]];
    if (ringLeft && ringRight && typeof ringLeft === 'object' && typeof ringRight === 'object') {
        const leftId = getItemId(ringLeft);
        const rightId = getItemId(ringRight);
        if (leftId && leftId === rightId) {
            const setId = getItemSetId(ringLeft);
            if (setId > 0) {
                return {
                    valid: false,
                    reason: {
                        code: 'DUPLICATE_SET_RING',
                        slot: RING_SLOTS[0],
                        slots: [...RING_SLOTS],
                        itemId: leftId,
                    },
                };
            }
        }
    }

    // 3) Duplicate trophy/dofus (slots 10..15)
    const occurrencesById = new Map();
    for (const slot of TROPHUS_SLOTS) {
        const item = items[slot];
        if (!item || typeof item !== 'object') continue;
        const id = getItemId(item);
        if (!id) continue;
        if (!occurrencesById.has(id)) occurrencesById.set(id, []);
        occurrencesById.get(id).push(slot);
    }

    for (const [id, slots] of occurrencesById.entries()) {
        if (slots.length <= 1) continue;
        return {
            valid: false,
            reason: {
                code: 'DUPLICATE_TROPHUS_DOFUS',
                itemId: id,
                slot: slots[0],
                slots,
            },
        };
    }

    // 4) Restricted trophy rule
    // Backend: if any equipped trophy/dofus has restricted === true,
    // then build invalid when equipped sets >= RESTRICTED_TROPHY_MAX_SETS.
    const hasRestrictedTrophy = TROPHUS_SLOTS.some((slot) => items[slot]?.restricted === true);
    if (hasRestrictedTrophy) {
        const firstRestrictedSlot = TROPHUS_SLOTS.find((slot) => items[slot]?.restricted === true);
        const equippedSetCount = countEquippedSets(build);
        if (equippedSetCount >= RESTRICTED_TROPHY_MAX_SETS) {
            return {
                valid: false,
                reason: {
                    code: 'RESTRICTED_TROPHY_SET_BONUS',
                    equippedSetCount,
                    slot: firstRestrictedSlot ?? null,
                },
            };
        }
    }

    // 5) General equip criteria check (characteristic conditions).
    // Vitality criteria need the character level to undo the base PV in the sheet row.
    const critLvl = safeReqLvl || toFiniteNum(build?.lvl) || 0;
    const unmetCriteria = findFirstUnmetItemCriteria(items, stats, critLvl);
    if (unmetCriteria) {
        return {
            valid: false,
            reason: {
                code: 'ITEM_CRITERIA_UNMET',
                slot: unmetCriteria.slot,
                itemId: getItemId(unmetCriteria.item),
            },
        };
    }

    // 6) AP/MP restricted items
    // Backend: if at least one AP/MP-restricted item is equipped,
    // build invalid when AP >= 12 AND MP >= 6.
    const hasApMpRestrictedItem = items.some((it) => isApMpRestrictedItem(it));
    if (hasApMpRestrictedItem) {
        const firstRestrictedSlot = items.findIndex((it) => isApMpRestrictedItem(it));
        const ap = stats?.[st.AP_ID]?.current;
        const mp = stats?.[st.MP_ID]?.current;
        const apN = toFiniteNum(ap) ?? 0;
        const mpN = toFiniteNum(mp) ?? 0;

        if (apN >= 12 && mpN >= 6) {
            return {
                valid: false,
                reason: {
                    code: 'AP_MP_RESTRICTED',
                    slot: firstRestrictedSlot >= 0 ? firstRestrictedSlot : null,
                    ap: apN,
                    mp: mpN,
                },
            };
        }
    }

    // 7) Forgemagie past +1 AP / MP / Range in total, exos included. The
    // sheet already takes the extra point off (Build._calculateStats); this is
    // what tells the user their maging is wasted. Names the first item adding
    // to the characteristic that went over.
    const deltasBySlot = items.map((item, slot) => {
        const lines = item && typeof build?.getItemMaging === 'function' ? build.getItemMaging(slot) : null;
        return lines ? magingDelta(item, lines) : null;
    });
    const excess = magingBonusExcess(null, deltasBySlot.filter(Boolean));
    const overId = Object.keys(excess).map(Number)[0];
    if (overId != null) {
        const slot = deltasBySlot.findIndex((delta) => (delta?.[overId] || 0) > 0);
        return {
            valid: false,
            reason: {
                code: 'MAGING_BONUS_OVER',
                slot: slot >= 0 ? slot : null,
                statId: overId,
                total: MAGING_BONUS_CAPS[overId] + excess[overId],
                cap: MAGING_BONUS_CAPS[overId],
            },
        };
    }

    return { valid: true };
}

function getLocalizedItemName(item, lang) {
    const n = item?.name;
    if (!n) return '';
    if (typeof n === 'object') return n[lang] || n.fr || n.en || '';
    return String(n);
}

function formatReason(reason, { lang, build, reqLvl }) {
    const template = TXT_REASON[reason.code];
    if (!template) return pick(TXT_REASON_FALLBACK, lang);

    const items = Array.isArray(build?.items) ? build.items : [];
    const slot = reason.slot ?? reason.slots?.[0] ?? null;
    const itemName = slot != null ? getLocalizedItemName(items[slot], lang) : '';

    const values = {
        item: itemName || pick(TXT_SOME_ITEM, lang),
        itemLevel: reason.itemLevel ?? '',
        reqLvl: reason.reqLvl ?? reqLvl ?? '',
        equippedSetCount: reason.equippedSetCount ?? 0,
        ap: reason.ap ?? '',
        mp: reason.mp ?? '',
        stat: reason.statId != null ? pick(st.STATS.find((s) => s.id === reason.statId)?.label || {}, lang) || '' : '',
        total: reason.total ?? '',
        cap: reason.cap ?? '',
    };
    const sentence = pick(template, lang).replace(/\{(\w+)\}/g, (m, key) => (
        key in values ? String(values[key]) : m
    ));
    return sentence.charAt(0).toUpperCase() + sentence.slice(1);
}

/**
 * One-stop validity description for the UI: runs the check, decides whether the
 * problem blocks generation, and localizes it. Returns null when the build is
 * valid.
 *
 * `blocking` (severity `error`) means the solver cannot recover on its own;
 * otherwise it is a `warning` the solver fixes by refilling unlocked slots.
 *
 * @returns {{ code: string, slot: number|null, blocking: boolean,
 *   severity: 'error'|'warning', title: string, detail: string, hint: string } | null}
 */
export function describeInitialBuildValidity(build, { reqLvl, lockedSlots = {}, lang = 'en' } = {}) {
    if (!build) return null;
    const result = validateBackendInitialBuild(build, { reqLvl });
    if (result.valid) return null;

    const { reason } = result;
    const blocking = isBlockingInitialBuildReason(reason, lockedSlots);
    return {
        code: reason.code,
        slot: reason.slot ?? reason.slots?.[0] ?? null,
        blocking,
        severity: blocking ? 'error' : 'warning',
        title: UNTITLED_REASONS.has(reason.code)
            ? ''
            : pick(blocking ? TXT_TITLE_BLOCKING : TXT_TITLE_WARNING, lang),
        detail: formatReason(reason, { lang, build, reqLvl }),
        hint: blocking ? pick(TXT_HINT_BLOCKING, lang) : '',
    };
}

